import { useFileUpload } from "@/components/ui/baseUi/use-file-upload"
import { cn } from "@/modules/lib/utils"
import { forwardRef, useImperativeHandle } from "react"

export type DropzoneRef = {
  openFileDialog: () => void
}

const Dropzone = forwardRef<
  DropzoneRef,
  {
    onFilesAdded: (files: File[]) => void
    disabled?: boolean
    accept?: string
  } & React.ComponentProps<"div">
>(function Dropzone({ onFilesAdded, children, className, disabled, accept, ...props }, ref) {
  const [
    { files, isDragging },
    { openFileDialog, getInputProps, handleDragEnter, handleDragLeave, handleDragOver, handleDrop },
  ] = useFileUpload({
    accept: accept ?? "image/*",
    maxFiles: 1,
    onFilesAdded: (addedFiles) => {
      onFilesAdded(addedFiles.map(({ file }) => (file instanceof File ? file : null)).filter((i) => i !== null))
    },
  })

  useImperativeHandle(ref, () => ({ openFileDialog }), [openFileDialog])

  const previewUrl = files[0]?.preview ?? null

  return (
    <div className={cn("inline-flex", className)} {...props}>
      {/* Drop area */}
      <button
        type="button"
        onClick={openFileDialog}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-dragging={isDragging || undefined}
        aria-label={previewUrl ? "Change image" : "Upload image"}
        className="w-full"
        disabled={disabled}
      >
        {children}
      </button>

      <input {...getInputProps()} className="sr-only" aria-label="Upload image file" tabIndex={-1} />
    </div>
  )
})

export default Dropzone
